import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { SharedLayout } from '../components/layout/SharedLayout';
import { theme } from '../styles/theme';

const HeroSection = styled.section`
  background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%);
  padding: 100px 20px 80px;
  color: white;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('/650f1c8c6cf62f0b696c3d20_Lake2.png');
    background-size: cover;
    background-position: center;
    opacity: 0.1;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const PageTitle = styled.h1`
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 800;
  margin-bottom: 20px;
  letter-spacing: -1px;
  text-align: center;
`;

const PageSubtitle = styled.p`
  font-size: clamp(18px, 2.5vw, 24px);
  opacity: 0.9;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.5;
  text-align: center;
`;

const IntroSection = styled.section`
  padding: 80px 20px;
  background: white;
`;

const IntroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
`;

const IntroTitle = styled.h2`
  font-size: clamp(28px, 3.5vw, 36px);
  font-weight: 800;
  color: #333;
  margin-bottom: 25px;
`;

const IntroText = styled.p`
  font-size: 18px;
  color: #666;
  line-height: 1.8;
  margin-bottom: 20px;
`;

const CompaniesSection = styled.section`
  padding: 80px 20px;
  background: #f8f9fa;
`;

const CompaniesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const CompanyCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 5px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  }
`;

const CompanyLogo = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%);
  border-radius: 16px;
  margin-bottom: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 40px;
    height: 40px;
    background: rgba(255, 215, 0, 0.2);
    border-radius: 50%;
  }
`;

const CompanyName = styled.h3`
  font-size: 24px;
  font-weight: 800;
  color: #333;
  margin-bottom: 15px;
`;

const CompanyTagline = styled.div`
  font-size: 14px;
  color: #ffd700;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 20px;
`;

const CompanyDescription = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.7;
  margin-bottom: 25px;
`;

const CompanyFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 25px 0;
`;

const CompanyFeature = styled.li`
  font-size: 15px;
  color: #555;
  margin-bottom: 12px;
  padding-left: 25px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 7px;
    width: 12px;
    height: 12px;
    background: #ffd700;
    border-radius: 50%;
  }
`;

const CompanyLink = styled.a`
  color: #232B3B;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  transition: all 0.3s;
  
  &:hover {
    color: #ffd700;
    
    &::after {
      transform: translateX(5px);
    }
  }
  
  &::after {
    content: '→';
    margin-left: 8px;
    transition: transform 0.3s;
  }
`;

const PartnerSection = styled.section`
  padding: 80px 20px;
  background: white;
`;

const PartnerHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const PartnerTitle = styled.h2`
  font-size: clamp(32px, 4vw, 42px);
  font-weight: 800;
  color: #333;
  margin-bottom: 20px;
`;

const PartnerSubtitle = styled.p`
  font-size: 20px;
  color: #666;
  max-width: 700px;
  margin: 0 auto;
`;

const PartnerLogos = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 40px;
  max-width: 1000px;
  margin: 0 auto;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
  }
`;

const PartnerLogo = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  transition: all 0.3s;
  
  &:hover {
    background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
    transform: scale(1.05);
  }
`;

const PartnerName = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  text-align: center;
`;

const SynergySection = styled.section`
  background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%);
  padding: 100px 20px;
  color: white;
`;

const SynergyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 40px;
  max-width: 1000px;
  margin: 0 auto;
`;

const SynergyCard = styled.div`
  text-align: center;
`;

const SynergyIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  border-radius: 50%;
  margin: 0 auto 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 40px;
    height: 40px;
    background: rgba(35, 43, 59, 0.3);
    border-radius: 50%;
  }
`;

const SynergyTitle = styled.h3`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 15px;
`;

const SynergyText = styled.p`
  font-size: 16px;
  line-height: 1.6;
  opacity: 0.9;
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  padding: 80px 20px;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: clamp(32px, 4vw, 42px);
  font-weight: 800;
  color: #333;
  margin-bottom: 20px;
`;

const CTAText = styled.p`
  font-size: 20px;
  color: #333;
  margin-bottom: 40px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled(Link)`
  background: #232B3B;
  color: white !important;
  padding: 18px 40px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: bold;
  font-size: 18px;
  display: inline-block;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(35, 43, 59, 0.3);
    color: white !important;
  }
`;

export const FamilyOfCompanies: React.FC = () => {
  return (
    <SharedLayout>
      <HeroSection>
        <Container>
          <PageTitle>Our Family of Companies</PageTitle>
          <PageSubtitle>
            A comprehensive network of financial services built on trust, expertise, and innovation
          </PageSubtitle>
        </Container>
      </HeroSection>

      <IntroSection>
        <Container>
          <IntroContent>
            <IntroTitle>Strength Through Specialization</IntroTitle>
            <IntroText>
              The Averill family of companies represents decades of experience across multiple 
              facets of financial planning and retirement security. Each company in our network 
              specializes in specific areas, allowing us to provide comprehensive solutions while 
              maintaining the highest level of expertise in each domain.
            </IntroText>
            <IntroText>
              Together, we serve over 10,000 clients nationwide, managing billions in retirement 
              assets with a perfect record of client satisfaction.
            </IntroText>
          </IntroContent>
        </Container>
      </IntroSection>


      <PartnerSection>
        <Container>
          <PartnerHeader>
            <PartnerTitle>Our Trusted Partners</PartnerTitle>
            <PartnerSubtitle>
              Working with industry leaders to provide you with the best solutions
            </PartnerSubtitle>
          </PartnerHeader>
          
          <PartnerLogos>
            <PartnerLogo>
              <PartnerName>Allianz</PartnerName>
            </PartnerLogo>
            <PartnerLogo>
              <PartnerName>American Equity</PartnerName>
            </PartnerLogo>
            <PartnerLogo>
              <PartnerName>Athene</PartnerName>
            </PartnerLogo>
            <PartnerLogo>
              <PartnerName>F&G</PartnerName>
            </PartnerLogo>
            <PartnerLogo>
              <PartnerName>Lincoln Financial</PartnerName>
            </PartnerLogo>
            <PartnerLogo>
              <PartnerName>Nationwide</PartnerName>
            </PartnerLogo>
          </PartnerLogos>
        </Container>
      </PartnerSection>

      <SynergySection>
        <Container>
          <PartnerHeader>
            <PartnerTitle style={{ color: 'white' }}>The Power of Integration</PartnerTitle>
            <PartnerSubtitle style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              How our companies work together to serve you better
            </PartnerSubtitle>
          </PartnerHeader>
          
          <SynergyGrid>
            <SynergyCard>
              <SynergyIcon />
              <SynergyTitle>Comprehensive Solutions</SynergyTitle>
              <SynergyText>
                Access expertise across all areas of financial planning through a single trusted relationship
              </SynergyText>
            </SynergyCard>
            
            <SynergyCard>
              <SynergyIcon />
              <SynergyTitle>Seamless Coordination</SynergyTitle>
              <SynergyText>
                Our companies work together to ensure your strategies are aligned and optimized
              </SynergyText>
            </SynergyCard>
            
            <SynergyCard>
              <SynergyIcon />
              <SynergyTitle>Shared Excellence</SynergyTitle>
              <SynergyText>
                Every company maintains our family's commitment to transparency, integrity, and results
              </SynergyText>
            </SynergyCard>
          </SynergyGrid>
        </Container>
      </SynergySection>

      <CTASection>
        <Container>
          <CTATitle>Experience the Averill Advantage</CTATitle>
          <CTAText>
            Discover how our family of companies can help secure your financial future
          </CTAText>
          <CTAButton to="/contact">
            Schedule Your Consultation
          </CTAButton>
        </Container>
      </CTASection>
    </SharedLayout>
  );
};